import { useCallback, useEffect, useRef } from 'react';
import { analytics } from '../analytics';

/**
 * User details interface for identification
 *
 * This interface should match the UserDetails type from @commander/user-details
 * Only includes fields needed for Mixpanel identification
 */
export interface UserDetailsForIdentification {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  timezone: string;
  country_code: string;
  phone_number: string | null;
  appearance: 'light' | 'dark' | 'system';
  communication_style: string;
  company?: {
    id: string;
    name: string;
    domain_name: string;
    logo_url: string | null;
    cro_admin_user_id: string;
  } | null;
  company_employee?: {
    id?: string;
    company_id?: string;
    user_id: string;
    app_access?: string[];
    role?: string;
    department?: string | null;
    job_title?: string | null;
    is_owner?: boolean;
    is_admin?: boolean;
    created_at?: string;
    updated_at?: string;
  } | null;
}

/**
 * Hook to identify users in Mixpanel after authentication
 *
 * This hook:
 * - Calls analytics.identify() with user details when authenticated
 * - Updates identification when user details change
 * - Calls analytics.reset() on logout to clear user data
 *
 * @param isAuthenticated - Whether the user is authenticated
 * @param userDetails - User details object (null if not authenticated)
 * @param isLoading - Whether user details are still loading
 *
 * @example
 * ```tsx
 * import { useAnalyticsIdentification } from '@commander/product-analytics';
 * import { useAuth } from '@commander/auth';
 * import { useUserDetails } from '@commander/user-details';
 *
 * function App() {
 *   const { isAuthenticated } = useAuth();
 *   const { userDetails, isLoading } = useUserDetails();
 *
 *   useAnalyticsIdentification(isAuthenticated, userDetails, isLoading);
 *
 *   return <Routes>...</Routes>;
 * }
 * ```
 */
export function useAnalyticsIdentification(
  isAuthenticated: boolean,
  userDetails: UserDetailsForIdentification | null | undefined,
  isLoading: boolean
): void {
  // Track the previous user ID to avoid re-identifying the same user
  const previousUserIdRef = useRef<string | null>(null);
  // Debounce timer ref
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const identifyUser = useCallback(() => {
    if (!userDetails || isLoading) {
      return;
    }

    const fullName = [userDetails.first_name, userDetails.last_name].filter(Boolean).join(' ');

    // Identify user in Mixpanel with all relevant traits
    analytics.identify(userDetails.id, {
      email: userDetails.email,
      name: fullName,
      first_name: userDetails.first_name,
      last_name: userDetails.last_name,
      timezone: userDetails.timezone,
      country_code: userDetails.country_code,
      phone_number: userDetails.phone_number,
      company_name: userDetails.company?.name || null,
      company_id: userDetails.company?.id || null,
      appearance: userDetails.appearance,
      communication_style: userDetails.communication_style,
      ...(userDetails.company_employee && {
        role: userDetails.company_employee.role,
        department: userDetails.company_employee.department,
        job_title: userDetails.company_employee.job_title,
        is_admin: userDetails.company_employee.is_admin,
        is_owner: userDetails.company_employee.is_owner,
      }),
    });

    if (userDetails.company?.id) {
      analytics.register({
        tenant_id: userDetails.company.id,
      });
    }

    previousUserIdRef.current = userDetails.id;
  }, [userDetails, isLoading]);

  useEffect(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Reset analytics state when user logs out
    if (!isAuthenticated) {
      analytics.reset();
      previousUserIdRef.current = null;
      return;
    }

    // Only identify when authenticated and user details are loaded
    if (isAuthenticated && userDetails && !isLoading) {
      // Skip if we're identifying the same user (avoid duplicate calls)
      if (previousUserIdRef.current === userDetails.id) {
        return;
      }

      // Debounce the identify call to prevent mutex timeout from rapid calls
      // This is especially important for React StrictMode double-invocations
      // and multiple micro-frontends calling identify simultaneously
      debounceTimerRef.current = setTimeout(() => {
        identifyUser();
      }, 100);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [isAuthenticated, userDetails, isLoading, identifyUser]);
}
